import React, { useMemo, useState } from 'react';
import {
  Button,
  TextField,
  DialogActions,
  DialogContent,
  Divider,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import AppSimpleDialog from 'app/shared-components/dialogs/AppSimpleDialog';
import FormDatePicker from 'app/shared-components/formComponents/FormDatePicker';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { deleteAffiliateLead, updateAffiliateLead } from 'src/app/api/AffiliateLeadsApi';
import { type AffiliateLeadEditDialogProps, dialogValidationSchema } from './types';

const AffiliateLeadEditDialog: React.FC<AffiliateLeadEditDialogProps> = ({
  open,
  handleClose,
  lead,
  onSave,
  onDelete,
}) => {
  const [formError, setFormError] = useState<string | null>(null);

  const defaultValues = useMemo(() => {
    return {
      contactDate: lead?.contactDate ? new Date(lead.contactDate) : new Date(),
      firstName: lead?.firstName || '',
      lastName: lead?.lastName || '',
      city: lead?.city || '',
      state: lead?.state || '',
      zipCode: lead?.zipCode || '',
      phoneNumber: lead?.phoneNumber || '',
      email: lead?.email || '',
      questions: lead?.questions || '',
      answers: lead?.answers || '',
      status: lead?.status || '',
      dba: lead?.dba || '',
      address: lead?.address || '',
    };
  }, [lead]);

  const methods = useForm({
    resolver: yupResolver(dialogValidationSchema),
    defaultValues,
    mode: 'onTouched',
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = methods;

  useMemo(() => {
    if (open) {
      reset(defaultValues);
      setFormError(null);
    }
  }, [defaultValues, open, reset]);

  const problemCount = Object.keys(errors).length;

  const onSubmit = async () => {
    const data = getValues();
    setFormError(null);

    if (!lead || !lead.id) {
      console.warn('Affiliate lead ID is missing.');
      handleClose();
      return;
    }

    try {
      const payload = {
        ...data,
        zipCode: `${data.zipCode}`,
        phoneNumber: `${data.phoneNumber}`,
      };

      const result = await updateAffiliateLead(lead.id, payload);
      if (result.success) {
        onSave?.(data);
        handleClose();
      } else {
        setFormError('Failed to update lead. Please try again.');
        console.error('Error updating affiliate lead:', result.data);
      }
    } catch (error) {
      setFormError('An issue occurred while saving. Please try again.');
      console.error('Error updating affiliate lead:', error);
    }
  };

  const handleDelete = async () => {
    if (!lead || !lead.id) {
      console.warn('Affiliate lead ID is missing.');
      handleClose();
      return;
    }

    try {
      const result = await deleteAffiliateLead(lead.id);
      if (result.success) {
        onDelete?.();
        handleClose();
      } else {
        setFormError('Failed to delete lead. Please try again.');
        console.error('Error deleting affiliate lead:', result.data);
      }
    } catch (error) {
      setFormError('An issue occurred while deleting. Please try again.');
      console.error('Error deleting affiliate lead:', error);
    }
  };

  return (
    <AppSimpleDialog
      open={open}
      handleClose={handleClose}
      title="Edit lead"
      size="md"
      scroll="paper"
    >
      <Divider />
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <DialogContent className="px-24 relative">
            {formError && (
              <Alert severity="error" className="mb-24">
                {formError}
              </Alert>
            )}

            <div className="flex flex-col gap-12">
              <div className="flex flex-col gap-12">
                <h3 className="">General Information</h3>
                <div className="flex gap-12">
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="First Name*"
                        variant="outlined"
                        fullWidth
                        error={!!errors.firstName?.message}
                        helperText={errors.firstName?.message}
                      />
                    )}
                  />
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Last Name*"
                        variant="outlined"
                        fullWidth
                        error={!!errors.lastName?.message}
                        helperText={errors.lastName?.message}
                      />
                    )}
                  />
                </div>
                <div className="flex gap-12">
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email*"
                        variant="outlined"
                        fullWidth
                        error={!!errors.email?.message}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                  <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone Number*"
                        variant="outlined"
                        fullWidth
                        error={!!errors.phoneNumber?.message}
                        helperText={errors.phoneNumber?.message}
                      />
                    )}
                  />
                </div>
                <div className="flex gap-12">
                  <Controller
                    name="contactDate"
                    control={control}
                    render={({ field }) => {
                      return (
                        <FormDatePicker
                          {...field}
                          id="contactDate"
                          control={control}
                          className="w-full"
                          label="Contact Date*"
                          maxDateValue={new Date()}
                          minDateValue={new Date(Date.UTC(1900, 0, 1))}
                          error={!!errors.contactDate?.message || false}
                        />
                      );
                    }}
                  />
                  <Controller
                    name="dba"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="DBA*"
                        variant="outlined"
                        fullWidth
                        error={!!errors.dba?.message}
                        helperText={errors.dba?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-12">
                <h3 className="">Address</h3>
                <div className="flex gap-12">
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="City*"
                        variant="outlined"
                        className="w-1/2"
                        error={!!errors.city?.message}
                        helperText={errors.city?.message}
                      />
                    )}
                  />
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="State*"
                        variant="outlined"
                        className="w-1/4"
                        error={!!errors.state?.message}
                        helperText={errors.state?.message}
                      />
                    )}
                  />
                  <Controller
                    name="zipCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Zip Code*"
                        variant="outlined"
                        className="w-1/4"
                        error={!!errors.zipCode?.message}
                        helperText={errors.zipCode?.message}
                      />
                    )}
                  />
                </div>
                <div className="flex gap-12">
                  <Controller
                    name="address"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Address*"
                        variant="outlined"
                        fullWidth
                        error={!!errors.address?.message}
                        helperText={errors.address?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-12">
                <h3 className="">Questions and Answers</h3>
                <div>
                  <Controller
                    name="questions"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Questions"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </div>
                <div>
                  <Controller
                    name="answers"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Answers"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions className="flex items-center mr-24 ml-12 my-20 p-0">
            {problemCount > 0 && (
              <Box className="relative flex-1 flex items-center mr-4 px-3 py-10 rounded-full overflow-hidden">
                <div className="absolute inset-0 bg-yellow-100 opacity-30 rounded-full pointer-events-none" />
                <WarningIcon className="text-yellow-600 mr-10 ml-10 z-10" fontSize="small" />
                <Typography variant="body2" className="text-yellow-800 z-10">
                  {problemCount} {problemCount === 1 ? 'problem' : 'problems'} in form
                </Typography>
              </Box>
            )}
            <Button
              type="button"
              className="flex items-center text-gray-600 font-500 bg-gray-50 hover:bg-gray-200 px-16 py-8 mr-16"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              className="flex items-center text-gray-600 font-500 bg-gray-50 hover:bg-gray-200 px-16 py-8 mr-16"
            >
              Delete lead
            </Button>
            <Button
              type="submit"
              className="text-white font-500 bg-blue-primary hover:bg-blue-primary-hover px-16 py-8"
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </AppSimpleDialog>
  );
};

export default AffiliateLeadEditDialog;
